import { useRoles } from '@/features/admin/api';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Calculator, Pencil, Plus, Trash2 } from 'lucide-react';
import { PageHeader, EmptyState } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageLoader } from '@/components/ui/loader';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useDeleteProperty, useProperties, useSaveProperty, type ObjectType, type Property, type PropertyType } from './records';

const OBJECTS: { key: ObjectType; label: string }[] = [
  { key: 'contact', label: 'Contact properties' },
  { key: 'company', label: 'Company properties' },
  { key: 'deal', label: 'Deal properties' },
  { key: 'lead', label: 'Lead properties' },
];

const TYPES: { value: PropertyType; label: string; hint: string }[] = [
  { value: 'TEXT', label: 'Single-line text', hint: 'A short answer, such as a nickname or a reference.' },
  { value: 'TEXTAREA', label: 'Multi-line text', hint: 'Longer notes.' },
  { value: 'NUMBER', label: 'Number', hint: 'Counts, sizes, scores.' },
  { value: 'CURRENCY', label: 'Currency (AED)', hint: 'Amounts in dirhams.' },
  { value: 'DATE', label: 'Date picker', hint: 'A day, such as a renewal date.' },
  { value: 'DATETIME', label: 'Date and time', hint: 'A moment, such as a viewing.' },
  { value: 'SELECT', label: 'Dropdown select', hint: 'One choice from a list you define.' },
  { value: 'MULTISELECT', label: 'Multiple checkboxes', hint: 'Several choices from a list.' },
  { value: 'CHECKBOX', label: 'Single checkbox', hint: 'Yes or no.' },
  { value: 'URL', label: 'Website URL', hint: 'A link.' },
  { value: 'EMAIL', label: 'Email', hint: 'A second email address.' },
  { value: 'PHONE', label: 'Phone number', hint: 'A second number.' },
  { value: 'USER', label: 'User', hint: 'A person in your team.' },
  { value: 'FORMULA', label: 'Calculation', hint: 'Worked out from other number properties.' },
];

/**
 * Settings → Properties (HubSpot) / custom fields (Kommo): the extra fields
 * each kind of record carries, with their type, choices or formula.
 */
export function PropertiesPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const object = (params.get('object') as ObjectType) || 'contact';
  const { data: properties = [], isLoading } = useProperties(object);
  const remove = useDeleteProperty(object);
  const { can } = usePermission();
  const [editing, setEditing] = useState<Property | 'new' | null>(null);

  return (
    <div>
      <PageHeader
        title="Properties"
        description="The extra fields on your contacts, companies, deals and leads. They appear on every record, in list columns, filters, imports and exports."
        actions={
          <Can permission="admin.customfield.create">
            <Button onClick={() => setEditing('new')}>
              <Plus /> {t('Create property')}
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap gap-1 rounded-lg border bg-card p-1">
        {OBJECTS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setParams({ object: item.key })}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors',
              object === item.key ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' : 'text-muted-foreground hover:bg-muted',
            )}
          >
            {t(item.label)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : properties.length === 0 ? (
        <EmptyState
          title="No custom properties yet"
          description="Add the fields your business tracks: property type, budget band, visa status, renewal date…"
          action={
            can('admin.customfield.create') ? (
              <Button onClick={() => setEditing('new')}>
                <Plus /> {t('Create property')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-start text-[12px] font-bold text-foreground/80">
              <tr>
                <th className="px-4 py-2.5 text-start">{t('Name')}</th>
                <th className="px-4 py-2.5 text-start">{t('Type')}</th>
                <th className="hidden px-4 py-2.5 text-start md:table-cell">{t('Group')}</th>
                <th className="hidden px-4 py-2.5 text-start md:table-cell">{t('Internal name')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {properties.map((property) => (
                <tr key={property.id} className="border-t">
                  <td className="px-4 py-3">
                    <div className="font-semibold">
                      {property.label}
                      {property.required && <span className="ms-1 text-destructive">*</span>}
                    </div>
                    {property.options?.description && <div className="text-xs text-muted-foreground">{property.options.description}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary">{t(TYPES.find((type) => type.value === property.type)?.label ?? property.type)}</Badge>
                    {property.type === 'FORMULA' && <code className="ms-2 text-xs text-muted-foreground">{property.options?.formula}</code>}
                    {property.options?.choices && <span className="ms-2 text-xs text-muted-foreground">{property.options.choices.length} {t('choices')}</span>}
                  </td>
                  <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">{property.options?.group || t('Custom properties')}</td>
                  <td className="hidden px-4 py-3 md:table-cell">
                    <code className="text-xs text-muted-foreground">{property.key}</code>
                  </td>
                  <td className="px-4 py-3 text-end">
                    <div className="flex justify-end gap-1">
                      <Can permission="admin.customfield.update">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(property)}>
                          <Pencil />
                        </Button>
                      </Can>
                      <Can permission="admin.customfield.delete">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => {
                            if (window.confirm(t('Delete "{name}"? The values stay on records and come back if you create it again.', { name: property.label }))) {
                              remove.mutate(property.id, { onError: (error) => toast.error(error instanceof ApiRequestError ? error.message : 'Could not delete') });
                            }
                          }}
                        >
                          <Trash2 />
                        </Button>
                      </Can>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <PropertyDialog object={object} property={editing === 'new' ? null : editing} numeric={properties.filter((p) => ['NUMBER', 'CURRENCY', 'FORMULA'].includes(p.type))} onClose={() => setEditing(null)} />}
    </div>
  );
}

function PropertyDialog({ object, property, numeric, onClose }: { object: ObjectType; property: Property | null; numeric: Property[]; onClose: () => void }) {
  const t = useT();
  const save = useSaveProperty(object);
  const [label, setLabel] = useState(property?.label ?? '');
  const [type, setType] = useState<PropertyType>(property?.type ?? 'TEXT');
  const [choices, setChoices] = useState((property?.options?.choices ?? []).join('\n'));
  const [formula, setFormula] = useState(property?.options?.formula ?? '');
  const [group, setGroup] = useState(property?.options?.group ?? '');
  const [description, setDescription] = useState(property?.options?.description ?? '');
  const [required, setRequired] = useState(property?.required ?? false);
  const [visibleTo, setVisibleTo] = useState<string[]>(property?.visibleToRoles ?? []);
  const [editableBy, setEditableBy] = useState<string[]>(property?.editableByRoles ?? []);
  const { data: roles = [] } = useRoles();
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    const options: Property['options'] = {
      ...(group.trim() ? { group: group.trim() } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(['SELECT', 'MULTISELECT'].includes(type) ? { choices: choices.split('\n').map((item) => item.trim()).filter(Boolean) } : {}),
      ...(type === 'FORMULA' ? { formula: formula.trim() } : {}),
    };
    save.mutate(
      { id: property?.id, label: label.trim(), ...(property ? {} : { type }), options, required, visibleToRoles: visibleTo, editableByRoles: editableBy },
      {
        onSuccess: () => {
          toast.success(t('Property saved'));
          onClose();
        },
        onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the property'),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{property ? t('Edit property') : t('Create property')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('Label')}</Label>
            <Input value={label} onChange={(event) => setLabel(event.target.value)} placeholder={t('e.g. Visa status')} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label>{t('Field type')}</Label>
            <Select value={type} onChange={(event) => setType(event.target.value as PropertyType)} disabled={Boolean(property)}>
              {TYPES.map((item) => (
                <option key={item.value} value={item.value} disabled={item.value === 'FORMULA' && numeric.length === 0}>
                  {t(item.label)}
                </option>
              ))}
            </Select>
            <p className="text-xs text-muted-foreground">{t(TYPES.find((item) => item.value === type)?.hint ?? '')}</p>
          </div>
          {['SELECT', 'MULTISELECT'].includes(type) && (
            <div className="space-y-1.5">
              <Label>{t('Choices, one per line')}</Label>
              <Textarea rows={5} value={choices} onChange={(event) => setChoices(event.target.value)} placeholder={'Gold\nSilver\nBronze'} />
            </div>
          )}
          {type === 'FORMULA' && (
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Calculator className="h-4 w-4" /> {t('Formula')}
              </Label>
              <Input value={formula} onChange={(event) => setFormula(event.target.value)} placeholder="{units} * {unit_price}" className="font-mono" />
              <div className="flex flex-wrap gap-1">
                {numeric.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setFormula(`${formula}{${item.key}}`)}
                    className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px] hover:border-primary"
                  >
                    {`{${item.key}}`}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{t('Use + - * / and brackets with number properties.')}</p>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t('Group')}</Label>
              <Input value={group} onChange={(event) => setGroup(event.target.value)} placeholder={t('Custom properties')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('Description')}</Label>
              <Input value={description} onChange={(event) => setDescription(event.target.value)} />
            </div>
          </div>
          {type !== 'FORMULA' && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={required} onCheckedChange={(checked) => setRequired(checked === true)} />
              {t('Required')}
            </label>
          )}
          {roles.length > 0 && (
            <details className="rounded-md border p-3 text-sm">
              <summary className="cursor-pointer font-semibold">{t('Field permissions')}</summary>
              <p className="mt-1 text-xs text-muted-foreground">{t('Leave both empty for everyone. Administrators always see and edit.')}</p>
              {[
                ['Who can see it', visibleTo, setVisibleTo],
                ['Who can change it', editableBy, setEditableBy],
              ].map(([title, value, setter]) => (
                <div key={String(title)} className="mt-3">
                  <p className="text-xs font-semibold">{t(String(title))}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {roles.map((role) => {
                      const list = value as string[];
                      const on = list.includes(role.key);
                      return (
                        <button
                          key={role.key}
                          type="button"
                          onClick={() => (setter as (next: string[]) => void)(on ? list.filter((key) => key !== role.key) : [...list, role.key])}
                          className={cn('rounded-full border px-2 py-0.5 text-xs font-semibold', on ? 'border-primary bg-primary text-white' : 'hover:bg-accent')}
                        >
                          {role.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </details>
          )}
          {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={submit} disabled={!label.trim() || save.isPending}>
            {t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
